// "If soil samples are uploaded without specified depth/extractant, use:" — these are what a soil
// sample is interpreted WITH when its own file doesn't say. They are not a preference and not a
// display setting P in Mehlich-3 and P in Bray are different numbers for the same soil, and a depth
// changes what a concentration means, so guessing one silently would corrupt every downstream
// recommendation built on it.

/*
 *
 * Bray-1 converts as an identity; **Olsen is ~35% lower and takes a real multiplier**
 * (`OLSEN_TO_M3_P = 1.35`). So Olsen is the single choice where that machinery changes a number --
 * and it distinction from a FILE via `p_extractant_raw`; from a picker there is nothing to recover
 * it from, because the click is the only evidence that will ever exist.
 */
export const P_EXTRACTANTS = [
  { value: "m3", label: "M3" },
  { value: "bray", label: "Bray P1" },
  { value: "olsen", label: "Olsen" },
] as const;

// So the token has to name both procedures, or every micro value lands attributed to a method that
// cannot produce it -- the wrong-number-not-missing-number failure this whole picker exists to
// prevent.
export const K_EXTRACTANTS = [
  { value: "m3", label: "M3" },
  { value: "ammonium_acetate_dtpa", label: "Ammonium Acetate/DTPA" },
] as const;

export type PExtractant = "m3" | "bray_olsen" | "bray" | "olsen";

/*
 *
 * Display labels for every token that can appear in STORED data — which is NOT the same set as the ones we
 * offer. `bray_olsen` and bare `ammonium_acetate` stay legal at the API and sit in existing rows; a file
 * can also state them honestly. Reading them back through the OFFER list would render the raw token at the
 * user, so the two lists are deliberately separate: one decides what a human may choose, this one decides
 * what a human can read.
 */
export const EXTRACTANT_LABELS: Record<string, string> = {
  m3: "M3",
  bray: "Bray P1",
  olsen: "Olsen",
  // not "Bray/Olsen": the token means the method was left UNDETERMINED, and saying so is the point
  bray_olsen: "Bray or Olsen (undetermined)",
  ammonium_acetate: "Ammonium Acetate",
  ammonium_acetate_dtpa: "Ammonium Acetate/DTPA",
};

/* * A readable name for a stored token; unknown tokens show themselves rather than being hidden. */
export function extractantLabel(token: string): string {
  return EXTRACTANT_LABELS[token] ?? token;
}
export type KExtractant = (typeof K_EXTRACTANTS)[number]["value"];

/* * What travels with the drop. Every field required — a partial set is not a usable fallback. */
export type SampleUnits = "ppm" | "lbs_ac";
export const UNITS_OPTIONS: { value: SampleUnits; label: string }[] = [
  { value: "ppm", label: "ppm" },
  { value: "lbs_ac", label: "lbs/acre" },
];

export interface SampleDefaults {
  /* * sampling depth in INCHES; decimals are normal (a 6.5" core is not a rounding error) */
  depth_in: number;
  p_extractant: PExtractant;
  k_extractant: KExtractant;
  /*
   *
   * what the file's numbers ARE — ppm or lbs/acre; normalized storage is always ppm.
   */
  units: SampleUnits;
}

/* * The in-progress form state: nothing chosen yet, which is the starting point by design. */
export interface SampleDefaultsDraft {
  depthText: string;
  p_extractant: PExtractant | null;
  k_extractant: KExtractant | null;
  units: SampleUnits | null;
}

export const EMPTY_SAMPLE_DEFAULTS: SampleDefaultsDraft = {
  depthText: "",
  p_extractant: null,
  k_extractant: null,
  units: null, // nothing pre-selected, same rule as the extractants: silence must not adopt a default
};

/* * Widest depth we'll accept, in inches. Deep profile cores run to a metre; past that it's a typo. */
export const MAX_DEPTH_IN = 60;

/*
 *
 * Depth in inches from raw input, or null if it isn't one.
 *
 * Returns null rather than 0 for unparseable input: 0 is a *valid-looking* depth that would sail through
 * a downstream `?? 0` and label every sample as taken at the surface.
 */
export function parseDepthIn(raw: string): number | null {
  const t = raw.trim();
  if (!t) return null;
  // reject anything that isn't a plain decimal — "8in", "6-8", "1e3" are all ambiguous, not depths
  if (!/^\d*\.?\d+$/.test(t)) return null;
  const n = Number(t);
  if (!Number.isFinite(n) || n <= 0 || n > MAX_DEPTH_IN) return null;
  return n;
}

/* * Why the form isn't ready, for the user — null when it is. */
export function sampleDefaultsProblem(d: SampleDefaultsDraft): string | null {
  if (parseDepthIn(d.depthText) === null) {
    return d.depthText.trim()
      ? `Depth must be a number of inches between 0 and ${MAX_DEPTH_IN}.`
      : "Choose a sampling depth.";
  }
  if (!d.p_extractant) return "Choose a phosphorus extractant.";
  if (!d.k_extractant) return "Choose a K / micros extractant.";
  if (!d.units)
    return "Choose the units the file reports in (ppm or lbs/acre).";
  return null;
}

/* * The draft as the value that travels, or null while it's incomplete. */
export function toSampleDefaults(
  d: SampleDefaultsDraft,
): SampleDefaults | null {
  const depth_in = parseDepthIn(d.depthText);
  if (depth_in === null || !d.p_extractant || !d.k_extractant || !d.units)
    return null;
  return {
    depth_in,
    p_extractant: d.p_extractant,
    k_extractant: d.k_extractant,
    units: d.units,
  };
}
